import React from 'react'
import styled, { css } from 'styled-components'
import { string, node, bool, array } from 'prop-types'
import { stripUnit } from 'polished'
import { Link } from 'gatsby'

import { H4, fontWeights, fontSizes } from 'styles/typography'
import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { borderRadius, square, cover } from 'styles/global'
import { borderColor, white } from 'styles/colors'
import Button from 'components/button/Button'

const Container = styled(Link)`
  display: flex;
  flex-direction: column;
  text-align: left;
  border-radius: ${stripUnit(borderRadius) * 2 + 'px'};
  color: ${borderColor};
  pointer-events: auto;
  background-color: #131922;
  flex: 1;
  width: 100%;
  overflow: hidden;

  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
    `}

  ${mediaDown.medium`
    text-align: center;
  `};
`

const FigureWrap = styled.div`
  position: relative;
  border-radius: ${stripUnit(borderRadius) * 2 + 'px'} ${stripUnit(borderRadius) * 2 + 'px'} 0 0;
  width: 100%;
  overflow: hidden;

  &:after {
    content: '';
    padding-bottom: 90px;
    width: 100%;
    display: block;

    ${media.small`
      padding-bottom: 130px;
    `};

    ${media.medium`
      padding-bottom: 62%;
    `};
  }
`

const Figure = styled.img`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;

  ${media.medium`
    ${cover('absolute')};
    object-fit: cover;
  `};
`

const Header = styled.header`
  width: 100%;
`

const Inner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${stripUnit(spacing.small) * 1.5 + 'px'} 0;

  ${media.small`
    padding: ${spacing.medium};
  `};

  ${media.medium`
    align-items: flex-start;
  `};

  ${media.large`
    padding: ${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};
`

const Title = styled.div`
  margin-bottom: ${spacing.small};

  ${media.medium`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${spacing.medium};
  `};
`

const Name = styled(H4)`
  color: ${white};
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: ${fontWeights.medium};

  ${media.medium`
    padding-right: ${spacing.small};
  `};

  ${media.medium`
    font-size: 1rem;
  `};

  ${media.xLarge`
    font-size: 1.2rem;
  `};
`

const Price = styled(H4)`
  color: ${white};
  text-transform: uppercase;
  font-weight: ${fontWeights.medium};
  flex-shrink: 0;

  ${mediaDown.medium`
    font-size: ${fontSizes.regular};
    font-weight: ${fontWeights.bold};
    margin-bottom: 0;
  `};

  ${media.medium`
    font-size: 1rem;
  `};

  ${media.xLarge`
    font-size: 1.2rem;
  `};
`

const Features = styled.p`
  margin-top: ${spacing.medium};
  font-size: ${fontSizes.tiny};
  flex: 1;

  ${mediaDown.medium`
    margin-top: ${spacing.small};
    margin-bottom: ${spacing.small};
  `};
`

const Colors = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: -3px;

  ${media.medium`
    margin: -${spacing.tiny};
  `};

  ${mediaDown.medium`
    justify-content: center;
  `};
`

const Swatch = styled.div`
  border-radius: 50%;
  margin: 3px;
  ${square('8px')};

  ${media.medium`
    margin: ${spacing.tiny};
    ${square('12px')};
  `};
`

const StyledButton = styled(Button)`
  ${media.medium`
    align-self: center;
  `};

  ${media.large`
    min-width: 160px;
  `};
`

const ProductCard = ({ name, price, colors, features, link, slug, disabled }) => {
  return (
    <Container to={link} disabled={disabled}>
      <FigureWrap>
        <Figure src={`${process.env.BASE_PATH}/images/products/epod/products/${slug}.jpg`} alt={name} />
      </FigureWrap>
      <Inner>
        <Header>
          <Title>
            <Name>{name}</Name>
            <Price className="u-hide-medium-down">{price}</Price>
          </Title>

          <Colors>
            {colors.map((color, i) => (
              <Swatch key={i} style={{ backgroundColor: color }} />
            ))}
          </Colors>
        </Header>
        <Features>{features}</Features>
        <Price className="u-hide-medium-up">{price}</Price>

        <StyledButton
          className="u-hide-medium-down"
          theme="custom"
          customColor={white}
          variant="outline"
          disabled={disabled}
        >
          {disabled ? 'Coming soon' : 'Buy now'}
        </StyledButton>
      </Inner>
    </Container>
  )
}

ProductCard.propTypes = {
  slug: string.isRequired,
  name: string.isRequired,
  price: string.isRequired,
  colors: array.isRequired,
  disabled: bool,
  features: node.isRequired,
  link: string.isRequired
}

export default ProductCard
